import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth'
import User from '@/models/User'

export const AUTH_START_PATH =
	(process.env.VUE_APP_API_URL || '/api/v3/') + 'auth/authenticator?phase=initiate-login&auto=1'
const AUTH_LOGOUT_PATH = (process.env.VUE_APP_API_URL || '/api/v3/') + 'auth/authenticator?phase=logout'

export async function hasValidSession() {
	if (globalThis.localStorage.getItem('isTesting')) {
		return true
	}
	try {
		await getCurrentUser()
		return true
	} catch (e) {
		return false
	}
}

/**
 * Whether an API call would go out with an access token, without redirecting to the login page.
 * For route guards that run before router.beforeResolve (ensureValidSession), so they do not fire
 * a request that is bound to fail with a 401 for a logged-out visitor or a dead Cognito session.
 */
export async function hasActiveSession(): Promise<boolean> {
	if (globalThis.localStorage.getItem('isTesting')) {
		return true
	}
	try {
		await getCurrentUser()
		const { tokens } = await fetchAuthSession()
		return !!tokens?.accessToken
	} catch {
		return false
	}
}

export async function ensureValidSession(opts?: { redirectTo?: string; returnTo?: string }) {
	if (!globalThis.localStorage.getItem('isTesting')) {
		try {
			await getCurrentUser()
			const { tokens } = await fetchAuthSession()
			if (!tokens?.accessToken) {
				redirectToAuth(opts)
				return false
			}
		} catch {
			redirectToAuth(opts)
			return false
		}
	}

	try {
		await User.fetchMeLazy()
	} catch (e: any) {
		if (e.message.includes('401')) {
			redirectToAuth(opts)
			return false
		}
		throw e
	}

	return true
}

/**
 * Sign the current account out and come straight back to `returnTo` as the account the
 * shared link was addressed to. `user` seeds the recipient's Cognito pool on the way back
 * in (the authenticator carries both across the Cognito logout redirect, which cannot take
 * query params of its own because its logout_uri is registered verbatim).
 */
export function switchAccount(opts: { returnTo: string; user?: string | null }) {
	// @ts-ignore
	globalThis.NREUM?.setUserId(null)

	let url = `${AUTH_LOGOUT_PATH}&returnTo=${encodeURIComponent(opts.returnTo)}`
	if (opts.user) {
		url += `&user=${encodeURIComponent(opts.user)}`
	}

	globalThis.location.assign(url)
}

export async function logout() {
	// @ts-ignore
	globalThis.NREUM?.setUserId(null)
	globalThis.location.assign(AUTH_LOGOUT_PATH)
}

function redirectToAuth(opts?: { redirectTo?: string; returnTo?: string }) {
	const redirectBase = opts?.redirectTo ?? AUTH_START_PATH
	const returnTo = opts?.returnTo ?? globalThis.location.href
	let url = `${redirectBase}&returnTo=${encodeURIComponent(returnTo)}`

	const currentQueryParams = new URLSearchParams(window.location.search)
	if (currentQueryParams.has('user')) {
		url += `&user=${encodeURIComponent(currentQueryParams.get('user') || '')}`
	}

	globalThis.location.assign(url)
}
